import {t,onLanguageChange,localeState} from './i18n.js';
import styles from './download-widget.css?raw';
import {publicationPaths} from './publication-paths.js';
import {safeHtml} from './document.js';
import {policy} from './policy.js';

export async function publicationPdf(book,options){
  // Print a sanitized copy, never the live reader or its highlights and controls.
  const chapters=book.chapters.map(c=>{const body=document.createElement('div');body.innerHTML=safeHtml(c.html,book.assetBase);body.querySelectorAll('.book-video-shell,iframe').forEach(el=>el.remove());return {...c,html:body.innerHTML};});
  let fontCSS='';
  const collect=sheet=>{try{for(const rule of sheet.cssRules){if(rule.type===CSSRule.IMPORT_RULE)collect(rule.styleSheet);else if(rule.type===CSSRule.FONT_FACE_RULE)fontCSS+=rule.cssText.replace(/url\(["']?([^\s"')]+)["']?\)/g,(_,url)=>`url("${new URL(url,sheet.href||document.baseURI).href}")`);}}catch{}};
  for(const sheet of document.styleSheets)collect(sheet);
  return policy('printHtml',{book:{title:book.title,language:book.language,direction:book.direction,author:book.author,license:book.license,sourceUrl:book.sourceUrl,chapters},options:options||{},fontCSS});
}
export async function openPdfPreview(book,options={}){
  const popup=window.open('','_blank');
  if(!popup)throw Error(t("ui-allow-popups-to-open-your-pdf-preview"));
  // Keep access long enough to prepare the local document, then sever opener.
  popup.opener=null;
  let html;try{html=await publicationPdf(book,options);}catch(error){popup.close();throw error;}
  popup.document.open();popup.document.write(html);popup.document.close();
  popup.document.getElementById('save-pdf').addEventListener('click',async()=>{await Promise.all([popup.document.fonts.ready,...[...popup.document.images].map(image=>image.decode().catch(()=>{}))]);popup.print();});
  return popup;
}

// The element is a browser adapter. Elm owns all widget state and markup.
class BookDownloads extends HTMLElement{
  constructor(){super();this.attachShadow({mode:'open'});}
  set book(book){this._book=book;if(this.isConnected)this.mount();}
  get book(){return this._book;}
  connectedCallback(){
    this.localeSubscription=onLanguageChange(()=>{this.localize();this.send('locale',this.messages());});
    if(this.book)this.mount();
  }
  disconnectedCallback(){this.localeSubscription?.();this.app?.ports.downloadRequest.unsubscribe(this.handleRequest);this.app=null;}
  messages(){return {...localeState().messages,'ui-download-2':t('ui-download-2',{value1:'{value1}'})};}
  localize(){this.lang=localeState().language;this.dir=localeState().direction;}
  send(tag,data=null){this.app?.ports.downloadResponse.send({tag,data});}
  mount(){
    this.app?.ports.downloadRequest.unsubscribe(this.handleRequest);
    this.localize();const style=document.createElement('style');style.textContent=styles;
    const node=document.createElement('div');this.shadowRoot.replaceChildren(style,node);
    const publication=publicationPaths(this.book);
    this.downloadBase=new URL(publication.base,document.baseURI);
    const app=window.Elm.Downloads.init({node,flags:{base:this.downloadBase.href,stem:publication.stem,messages:this.messages()}});this.app=app;
    const book=this.book;
    this.handleRequest=message=>{
      const send=(tag,data)=>{if(this.app===app&&this.book===book&&this.isConnected)this.send(tag,data);};
      this.effect(message.tag,book,send).catch(error=>send('error',error.message));
    };
    app.ports.downloadRequest.subscribe(this.handleRequest);
    this.shadowRoot.onkeydown=event=>event.stopPropagation();
  }
  async effect(tag,book,send){
    switch(tag){
      case'typeset':{
        const opener=this.shadowRoot.querySelector('.settings');
        const {openTypesetter}=await import('./typesetter.js');
        if(this.isConnected&&this.book===book)openTypesetter(book,{chapter:Number(this.closest('.book-chapter')?.dataset.index)||0,opener});
        send('typesetOpened');return;
      }
      case'share':{
        let url=new URL('./',document.baseURI).href;
        try{const source=new URL(book.sourceUrl);if(['https:','http:'].includes(source.protocol))url=source.href;}catch{}
        if(typeof navigator.share==='function'){
          try{await navigator.share({title:book.title,url});send('shared');return;}
          catch(error){if(error.name==='AbortError'){send('shared');return;}}
        }
        try{await navigator.clipboard.writeText(url);send('copied');}
        catch{send('shareLink',url);}return;
      }
      case'selectShareLink':requestAnimationFrame(()=>{const input=this.shadowRoot.querySelector('.share-link');if(input&&!input.hidden){input.focus();input.select();}});return;
    }
  }
}
customElements.define('book-downloads',BookDownloads);
export function downloadWidget(book){const widget=document.createElement('book-downloads');widget.book=book;return widget;}
